import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import OrgManager from '../services/OrgManager';

const ROLE_COLORS = {
  owner:  '#f59e0b',
  admin:  '#8b5cf6',
  member: '#3b82f6',
  viewer: '#6b7280',
};

const ROLES_TO_ASSIGN = ['viewer', 'member', 'admin'];

export default function OrgMembersScreen() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg, myRole } = useOrg();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canManage = myRole === 'owner' || myRole === 'admin';

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    try {
      const data = await OrgManager.listMembers(activeOrg.id);
      setMembers(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeOrg]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (listenerId, role) => {
    try {
      await OrgManager.changeMemberRole(activeOrg.id, listenerId, role);
      load();
    } catch (e) {
      alert(`Failed to change role: ${e.message}`);
    }
  };

  const remove = async (listenerId) => {
    if (!window.confirm(t('app.org.confirmRemoveMember'))) return;
    try {
      await OrgManager.removeMember(activeOrg.id, listenerId);
      load();
    } catch (e) {
      alert(`Failed to remove member: ${e.message}`);
    }
  };

  if (!activeOrg) return (
    <View style={styles.center}><Text style={{ color: theme.textSecondary }}>{t('app.org.noActiveOrg')}</Text></View>
  );

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.background }]}
                contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.text }]}>{t('app.org.teamMembers')}</Text>
        {canManage && (
          <TouchableOpacity
            style={[styles.inviteBtn, { backgroundColor: theme.accent }]}
            onPress={() => window.history.pushState({}, '', '/org/invite')}
          >
            <Text style={styles.inviteBtnText}>{t('app.org.inviteBtn')}</Text>
          </TouchableOpacity>
        )}
      </View>

      {loading ? (
        <Text style={{ color: theme.textSecondary }}>{t('app.org.loading')}</Text>
      ) : error ? (
        <Text style={{ color: '#ef4444' }}>{error}</Text>
      ) : (
        members.map(m => (
          <View key={m.listener_id} style={[styles.memberCard, { backgroundColor: theme.cardBg, borderColor: theme.border }]}>
            <View style={styles.memberInfo}>
              <Text style={[styles.username, { color: theme.text }]}>@{m.username}</Text>
              <Text style={[styles.email, { color: theme.textSecondary }]}>{m.email}</Text>
              {m.joined_at && (
                <Text style={[styles.meta, { color: theme.textSecondary }]}>
                  {t('app.org.joinedDate', { date: new Date(m.joined_at).toLocaleDateString() })}
                </Text>
              )}
            </View>

            <View style={styles.memberActions}>
              <View style={[styles.roleBadge, { backgroundColor: (ROLE_COLORS[m.role] || '#6b7280') + '22' }]}>
                <Text style={[styles.roleText, { color: ROLE_COLORS[m.role] || '#6b7280' }]}>
                  {t(`app.org.role${m.role.charAt(0).toUpperCase() + m.role.slice(1)}`)}
                </Text>
              </View>

              {canManage && m.role !== 'owner' && (
                <>
                  <View style={styles.roleChips}>
                    {ROLES_TO_ASSIGN.map(r => (
                      <TouchableOpacity
                        key={r}
                        disabled={m.role === r}
                        onPress={() => changeRole(m.listener_id, r)}
                        style={[
                          styles.chip,
                          { borderColor: theme.border },
                          m.role === r && { backgroundColor: theme.accent + '33', borderColor: theme.accent },
                        ]}
                      >
                        <Text style={{ fontSize: 11, color: m.role === r ? theme.accent : theme.textSecondary }}>
                          {t(`app.org.role${r.charAt(0).toUpperCase() + r.slice(1)}`)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  <TouchableOpacity onPress={() => remove(m.listener_id)} style={styles.removeBtn}>
                    <Text style={{ color: '#ef4444', fontSize: 12 }}>{t('app.common.remove')}</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        ))
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 24, maxWidth: 720, alignSelf: 'center', width: '100%' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: '700' },
  inviteBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  inviteBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  memberCard: {
    borderWidth: 1, borderRadius: 12, padding: 16, marginBottom: 12,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  },
  memberInfo: { flex: 1 },
  username: { fontSize: 14, fontWeight: '600' },
  email: { fontSize: 12, marginTop: 2 },
  meta: { fontSize: 11, marginTop: 4 },
  memberActions: { alignItems: 'flex-end', gap: 8 },
  roleBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  roleText: { fontSize: 11, fontWeight: '700', textTransform: 'capitalize' },
  roleChips: { flexDirection: 'row', gap: 5 },
  chip: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 1, cursor: 'pointer' },
  removeBtn: { paddingVertical: 4, cursor: 'pointer' },
});
